import { useState, useEffect, useRef } from 'react';
import { Container, Button, Alert, Spinner, Card, Badge } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sampleService, Sample } from '../services/sample.service';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { canManageSample } from '../utils/samplePermissions';
import {
  canSampleAcceptUsageRecord,
  sampleRemainingQuantityBadgeBg,
} from '../utils/sampleUsageRecords';
import { getAxiosOrErrorMessage } from '../utils/error.utils';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import SampleDetailFactsPanels from '../components/samples/SampleDetailFactsPanels';
import SampleDetailHeroActions from '../components/samples/SampleDetailHeroActions';
import SampleExternalShareModal from '../components/samples/SampleExternalShareModal';
import SampleFormModal from '../components/samples/SampleFormModal';
import SampleDeleteModal from '../components/samples/SampleDeleteModal';
import UsageRecordFormModal, {
  type UsageRecordFormData,
} from '../components/samples/UsageRecordFormModal';
import { loadEditableSample } from '../hooks/useSampleEditLoader';
import { formatEntityDisplayName, softBreakIdentifier } from '../utils/softBreakIdentifier';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import { joinLabeledValue } from '../utils/joinLabeledValue';
import { SITE_NAME } from '../constants/siteSeo';
import { isSampleLocalMode } from '../services/systems/sample-local/sampleLocalMode';
import {
  readSampleRouterState,
  readSampleDetailSession,
  writeSampleDetailSession,
  clearSampleDetailSession,
  resolveSampleDetailId,
  resolveSampleDetailBackLabel,
  stripSampleRouterStateKeys,
  navigateToUsageHistory,
} from '../utils/samplePageNavigation';

export default function SampleDetail() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const sampleIdRef = useRef<number | null>(null);
  const preferAdminViewAllRef = useRef(false);
  const returnPathRef = useRef('/samples');
  const routerStateStrippedRef = useRef(false);
  if (sampleIdRef.current === null) {
    const routerState = readSampleRouterState(location);
    const session = readSampleDetailSession();
    sampleIdRef.current = resolveSampleDetailId(location);
    preferAdminViewAllRef.current =
      routerState.preferAdminViewAll === true || session?.preferAdminViewAll === true;
    if (typeof routerState.returnPath === 'string' && routerState.returnPath.startsWith('/')) {
      returnPathRef.current = routerState.returnPath;
    } else if (session?.returnPath) {
      returnPathRef.current = session.returnPath;
    }
    if (sampleIdRef.current != null) {
      writeSampleDetailSession({
        sampleId: sampleIdRef.current,
        ...(preferAdminViewAllRef.current ? { preferAdminViewAll: true } : {}),
        ...(returnPathRef.current !== '/samples' ? { returnPath: returnPathRef.current } : {}),
      });
    }
  }
  const sampleId = sampleIdRef.current;
  const returnPath = returnPathRef.current;
  const backLabel = resolveSampleDetailBackLabel(returnPath, t);
  const goBack = () => navigate(returnPath);
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { adminViewAll } = useAdminStorageViewAll(isAdmin);
  const effectiveAdminViewAll = adminViewAll || (isAdmin && preferAdminViewAllRef.current);
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showExternalShareModal, setShowExternalShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [editSample, setEditSample] = useState<Sample | null>(null);

  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ['samples', 'detail', sampleId, effectiveAdminViewAll],
    queryFn: () =>
      sampleService.getById(sampleId!, { adminViewAll: effectiveAdminViewAll || undefined }),
    enabled: sampleId != null,
  });

  useEffect(() => {
    if (routerStateStrippedRef.current) return;
    const routerState = readSampleRouterState(location);
    if (
      routerState.sampleId != null ||
      routerState.preferAdminViewAll != null ||
      routerState.returnPath != null
    ) {
      routerStateStrippedRef.current = true;
      stripSampleRouterStateKeys(navigate, location, [
        'sampleId',
        'preferAdminViewAll',
        'returnPath',
      ]);
    }
  }, [location, navigate]);

  const addUsageMutation = useMutation({
    mutationFn: (payload: UsageRecordFormData) =>
      sampleService.addUsageRecord(
        sampleId!,
        {
          quantity: payload.quantity,
          usageDate: payload.usageDate,
          purpose: payload.purpose,
          notes: payload.notes,
          action: payload.action,
        },
        { adminViewAll: effectiveAdminViewAll || undefined }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples', 'detail', sampleId] });
      queryClient.invalidateQueries({ queryKey: ['samples'] });
      setShowUsageModal(false);
      setSuccessMessage(t('usage_record.success'));
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (err: unknown) => {
      const raw = getAxiosOrErrorMessage(err) || t('usage_record.add_failed');
      const isKey =
        typeof raw === 'string' &&
        (raw.startsWith('sample.') ||
          raw.startsWith('usage_record.') ||
          raw.startsWith('common.') ||
          raw.startsWith('api.error.'));
      setError(isKey ? t(raw, { defaultValue: raw }) : raw);
      setSuccessMessage('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: Partial<Sample> }) =>
      sampleService.update(payload.id, payload.data, {
        adminViewAll: effectiveAdminViewAll || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples', 'detail', sampleId] });
      queryClient.invalidateQueries({ queryKey: ['samples'] });
      setShowEditModal(false);
      setEditSample(null);
      setSuccessMessage(t('sample.update_success'));
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (err: unknown) => {
      setError(getAxiosOrErrorMessage(err) || t('sample.update_failed'));
      setSuccessMessage('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      sampleService.delete(id, { adminViewAll: effectiveAdminViewAll || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples'] });
      clearSampleDetailSession();
      setShowDeleteModal(false);
      navigate(returnPath, { replace: true });
    },
    onError: (err: unknown) => {
      setError(getAxiosOrErrorMessage(err) || t('sample.delete_failed'));
      setSuccessMessage('');
    },
  });

  const sample: Sample | undefined = data?.data;
  const canManage = sample
    ? canManageSample(sample, user?.id, { isAdmin, adminViewAll: effectiveAdminViewAll })
    : false;
  const readOnlyTitle = t('sample.case_manager.read_only_other_user');

  useEffect(() => {
    if (!sample?.name) return;
    const prev = document.title;
    const displayName = formatEntityDisplayName(sample.name, t);
    document.title = `${displayName} · ${t('sample.detail_nav_short', { defaultValue: '詳細' })} · ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, [sample?.name, sample?.id, t]);

  const openEditModal = async () => {
    if (!sample || !canManage) return;
    const result = await loadEditableSample({
      sampleId: sample.id,
      userId: user?.id,
      isAdmin,
      adminViewAll: effectiveAdminViewAll,
      t,
      knownSample: sample,
      logContext: 'sample detail edit',
    });
    if (result.ok === false) {
      setError(result.error);
      return;
    }
    setEditSample(result.sample);
    setShowEditModal(true);
  };

  if (sampleId == null) {
    return (
      <Container fluid className="mt-2 px-3 px-md-4">
        <SampleManagerNavigation />
        <Alert
          variant="warning"
          className="mb-0 py-3 px-3 d-flex flex-column flex-sm-row align-items-sm-center gap-2 justify-content-between"
        >
          <span>{t('sample.detail_no_selection')}</span>
          <Button
            variant="primary"
            size="sm"
            className="flex-shrink-0 align-self-start align-self-sm-center"
            onClick={() => navigate('/samples')}
            aria-label={t('navigation.sample_management_short', { defaultValue: 'List' })}
            title={t('sample.back_to_list')}
          >
            {t('sample.back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container fluid className="mt-2 px-3 px-md-4">
        <SampleManagerNavigation />
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (isError || !sample) {
    return (
      <Container fluid className="mt-2 px-3 px-md-4">
        <SampleManagerNavigation />
        <Alert variant="danger">
          {queryError instanceof Error
            ? queryError.message.startsWith('api.error.')
              ? t(queryError.message, { defaultValue: queryError.message })
              : queryError.message
            : t('sample.load_failed')}
        </Alert>
        <Button variant="secondary" onClick={goBack} className="mt-3">
          {backLabel}
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className="mt-2 px-3 px-md-4">
        <SampleManagerNavigation />
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {successMessage ? (
          <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        ) : null}

        <Card className="mb-4" id="sample-detail-page">
          <Card.Header className="bg-primary text-white sample-detail-hero">
            <div className="sample-detail-hero-main min-w-0">
              {/* ページの単一 h1（見た目はヒーロー密度を維持） */}
              <h1 className="h5 mb-1 text-break sample-detail-hero-title">
                <i className="fas fa-flask me-2" aria-hidden="true" />
                {softBreakIdentifier(sample.name, t)}
              </h1>
              <div className="d-flex flex-wrap gap-1 align-items-center sample-detail-hero-meta">
                <Badge bg="light" text="dark" title={t('sample.detail_url_hint')}>
                  {joinLabeledValue(t('common.id'), String(sample.id))}
                </Badge>
                <Badge
                  bg={sampleRemainingQuantityBadgeBg(sample)}
                  className="sample-detail-hero-qty"
                  title={t('sample.remaining_quantity')}
                >
                  {joinLabeledValue(
                    t('sample.remaining_quantity_short', { defaultValue: '残' }),
                    formatSampleQuantityWithUnit(sample.remainingQuantity, sample.unit, t)
                  )}
                </Badge>
                {sample.disposalDate ? (
                  <Badge bg="dark">{t('sample_filter.status_disposed')}</Badge>
                ) : null}
              </div>
            </div>
            <SampleDetailHeroActions
              canManage={canManage}
              canAddUsage={canSampleAcceptUsageRecord(sample)}
              readOnlyTitle={readOnlyTitle}
              backLabel={backLabel}
              onAddUsage={() => setShowUsageModal(true)}
              onEdit={() => void openEditModal()}
              onDelete={() => setShowDeleteModal(true)}
              onShare={
                isSampleLocalMode() ? undefined : () => setShowExternalShareModal(true)
              }
              onBack={goBack}
            />
          </Card.Header>
          <Card.Body>
            {!canManage ? (
              <Alert variant="info" className="mb-3">
                {readOnlyTitle}
              </Alert>
            ) : null}
            {!canSampleAcceptUsageRecord(sample) ? (
              <Alert variant="warning" className="mb-3">
                {sample.disposalDate ? t('sample.already_disposed') : t('sample.cannot_add_usage')}
              </Alert>
            ) : null}

            <SampleDetailFactsPanels
              sample={sample}
              className="mb-0"
              onViewAllHistory={() => navigateToUsageHistory(navigate, sample.id)}
            />
          </Card.Body>
        </Card>
      </Container>

      <SampleExternalShareModal
        show={showExternalShareModal}
        onHide={() => setShowExternalShareModal(false)}
        sampleId={sampleId}
        adminViewAll={effectiveAdminViewAll}
      />

      {showEditModal && editSample ? (
        <SampleFormModal
          show
          onHide={() => {
            setShowEditModal(false);
            setEditSample(null);
            setError('');
          }}
          onSubmit={async (formData) => {
            await updateMutation.mutateAsync({ id: editSample.id, data: formData });
          }}
          initialData={editSample}
          isLoading={updateMutation.isPending}
          adminStorageViewAll={effectiveAdminViewAll}
        />
      ) : null}

      <SampleDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setError('');
        }}
        onConfirm={async () => {
          if (sampleId != null) {
            await deleteMutation.mutateAsync(sampleId);
          }
        }}
        sampleName={sample.name}
        isLoading={deleteMutation.isPending}
      />

      <UsageRecordFormModal
        show={showUsageModal}
        onHide={() => setShowUsageModal(false)}
        onSubmit={async (formData) => {
          await addUsageMutation.mutateAsync(formData);
        }}
        sampleId={sample.id}
        remainingQuantity={sample.remainingQuantity}
        unit={sample.unit}
        isLoading={addUsageMutation.isPending}
      />
    </>
  );
}
